import {
  useEffect,
  useMemo,
  useRef,
  type Dispatch,
  type SetStateAction,
} from "react"

import { resolveAgent } from "@/agents"
import { MAX_TAGS, normalizeTags } from "@/tags/values"
import { toast } from "@coval/ui"

import type { AgentRecord } from "@/types/api/agent"
import type { Persona } from "@/types/api/Persona"
import type { TestSetRecord } from "@/types/api/TestSetRecord"

import type {
  SimulationLaunchMetricRecordsByName,
  SimulationTemplateData,
} from "./types"

const UNLIMITED_TAGS = Number.MAX_SAFE_INTEGER
const EMPTY_TAGS: string[] = []
const TAG_KEY_SEPARATOR = "\u0000"
const NO_TEMPLATE_ID = "none"
const TAG_CAP_TOAST_DURATION_MS = 4000

interface ResourceWithTags {
  tags?: string[] | null
}

interface CollectLaunchResourceTagsOptions {
  agentsById: Record<string, AgentRecord>
  metricRecords: SimulationLaunchMetricRecordsByName
  personaRecords: Persona[]
  selectedTemplateId: string
  templateData: Pick<
    SimulationTemplateData,
    "agentIds" | "enabledMetrics" | "personaIds" | "testSetIds"
  >
  templateRecords: SimulationTemplateData[]
  testSetRecords: TestSetRecord[]
}

interface MergeLaunchResourceTagsResult {
  tags: string[]
  wasCapped: boolean
}

interface UseLaunchResourceTagsPrefillOptions {
  resourceTags: string[]
  setTags: Dispatch<SetStateAction<string[]>>
  tags: string[]
}

function getResourceTags(resource: ResourceWithTags | null | undefined) {
  return resource?.tags ?? EMPTY_TAGS
}

function areTagsEqual(left: string[], right: string[]) {
  return (
    left.length === right.length &&
    left.every((tag, index) => tag === right[index])
  )
}

function buildTagKey(tags: string[]) {
  return tags.join(TAG_KEY_SEPARATOR)
}

export function collectLaunchResourceTags({
  agentsById,
  metricRecords,
  personaRecords,
  selectedTemplateId,
  templateData,
  templateRecords,
  testSetRecords,
}: CollectLaunchResourceTagsOptions): string[] {
  const agentTags = (templateData.agentIds ?? []).flatMap((agentId) =>
    getResourceTags(resolveAgent(agentId, agentsById) as ResourceWithTags),
  )
  const personaTags = (templateData.personaIds ?? []).flatMap((personaId) =>
    getResourceTags(personaRecords.find((persona) => persona.id === personaId)),
  )
  const testSetTags = (templateData.testSetIds ?? []).flatMap((testSetId) =>
    getResourceTags(testSetRecords.find((testSet) => testSet.id === testSetId)),
  )
  const metricTags = (templateData.enabledMetrics ?? []).flatMap((metricName) =>
    getResourceTags(metricRecords[metricName]),
  )
  const templateTags =
    selectedTemplateId && selectedTemplateId !== NO_TEMPLATE_ID
      ? getResourceTags(
          templateRecords.find(
            (template) => template.templateId === selectedTemplateId,
          ),
        )
      : EMPTY_TAGS

  return normalizeTags(
    [
      ...agentTags,
      ...personaTags,
      ...testSetTags,
      ...metricTags,
      ...templateTags,
    ],
    UNLIMITED_TAGS,
  )
}

export function mergeLaunchResourceTags({
  currentTags,
  nextResourceTags,
  previousResourceTags,
}: {
  currentTags: string[]
  nextResourceTags: string[]
  previousResourceTags: string[]
}): MergeLaunchResourceTagsResult {
  const previousResourceTagSet = new Set(
    normalizeTags(previousResourceTags, UNLIMITED_TAGS),
  )
  const manualTags = normalizeTags(currentTags, UNLIMITED_TAGS).filter(
    (tag) => !previousResourceTagSet.has(tag),
  )
  const mergedTags = normalizeTags(
    [...manualTags, ...nextResourceTags],
    UNLIMITED_TAGS,
  )

  return {
    tags: mergedTags.slice(0, MAX_TAGS),
    wasCapped: mergedTags.length > MAX_TAGS,
  }
}

export function useLaunchResourceTagsPrefill({
  resourceTags,
  setTags,
  tags,
}: UseLaunchResourceTagsPrefillOptions) {
  const currentTagsRef = useRef(tags)
  const previousResourceTagsRef = useRef<string[]>([])
  const cappedToastKeyRef = useRef("")
  const resourceTagsKey = useMemo(
    () => buildTagKey(resourceTags),
    [resourceTags],
  )

  useEffect(() => {
    currentTagsRef.current = tags
  }, [tags])

  useEffect(() => {
    const normalizedResourceTags = normalizeTags(resourceTags, UNLIMITED_TAGS)
    const nextState = mergeLaunchResourceTags({
      currentTags: currentTagsRef.current,
      nextResourceTags: normalizedResourceTags,
      previousResourceTags: previousResourceTagsRef.current,
    })

    previousResourceTagsRef.current = normalizedResourceTags
    currentTagsRef.current = nextState.tags

    setTags((currentTags) => {
      const normalizedCurrentTags = normalizeTags(currentTags, UNLIMITED_TAGS)
      return areTagsEqual(normalizedCurrentTags, nextState.tags)
        ? currentTags
        : nextState.tags
    })

    if (nextState.wasCapped) {
      if (cappedToastKeyRef.current !== resourceTagsKey) {
        toast.warning({
          title: `Tags capped at ${MAX_TAGS}`,
          description: `Selected resources have more than ${MAX_TAGS} tags. Only the first ${MAX_TAGS} were added.`,
          duration: TAG_CAP_TOAST_DURATION_MS,
        })
        cappedToastKeyRef.current = resourceTagsKey
      }
      return
    }

    cappedToastKeyRef.current = ""
  }, [resourceTags, resourceTagsKey, setTags])
}
